import { useMutation } from "@apollo/react-hooks";
import { useAsyncCallback } from 'react-use-async-callback';
import gql from "graphql-tag";
import { AsyncActionStatus } from '../../../shared/abstractStore';
import { DriverMetricUpdateInput } from '../generated/globalTypes';
import { updateDriverMetricMutation, updateDriverMetricMutationVariables } from '../generated/updateDriverMetricMutation';
import { driverMetricFieldsFragment } from '../models/DriverMetric';

/**
 * Get a callback to update a DriverMetric in the store.
 */
export function useUpdateDriverMetricCallback(): [(id: string, model: DriverMetricUpdateInput) => Promise<void>, AsyncActionStatus] {
    const [mutationAction, { error }] = useMutation<updateDriverMetricMutation, updateDriverMetricMutationVariables>(
        gql`
            mutation updateDriverMetricMutation ($model: DriverMetricUpdateInput!) {
                updateDriverMetric(model: $model) {
                    ...driverMetricFields
                }
            }

            ${driverMetricFieldsFragment}
        `
    );

    // Wrap the mutation in a standard callback.
    const [action, { isExecuting, errors: actionErrors }] = useAsyncCallback(async (id: string, model: DriverMetricUpdateInput) => {
        const result = await mutationAction({ variables: { model: { ...model, id: id } } });
        if (result.errors && result.errors.length) {
            throw result.errors;
        }
    }, [mutationAction]);

    return [action, { isExecuting: isExecuting, errors: error ? error.message : actionErrors }];
}
